/*
  Attar de Astana · distributor page

  Two jobs, neither of which hides content.

  1. The form. Native validation stays the source of truth; this takes over the
     presentation once it has loaded: inline messages in sentence case, a submitting
     state, the honeypot guard, focus on the confirmation after a post, and the values
     put back if Shopify sends the page back with an email error (the form object only
     remembers the email, so the rest is kept in sessionStorage for that one round trip).
     With scripts off the browser's own validation bubbles do the job and the form posts.

  2. The header entrance, replayed when the theme editor reloads the section. The first
     paint is armed by the section's own inline script; this file never arms on load.
*/

(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const STORE = 'attar-distributor-form';

  /* ---------------------------------------------------------------- copy */

  const COPY = {
    valueMissing: (el) => el.dataset.errorRequired || 'This field is required.',
    typeMismatch: (el) =>
      el.dataset.errorType ||
      (el.type === 'url' ? 'Enter a web address starting with https://.' : 'Check the format and try again.'),
    patternMismatch: (el) => el.dataset.errorPattern || el.dataset.errorType || 'Check the format and try again.',
    tooShort: (el) => `Use at least ${el.minLength} characters.`,
    tooLong: (el) => `Use no more than ${el.maxLength} characters.`,
  };

  const message = (el) => {
    for (const key of Object.keys(COPY)) if (el.validity[key]) return COPY[key](el);
    return 'Check this field and try again.';
  };

  /* -------------------------------------------------------------- fields */

  const fieldOf = (el) => el.closest('[data-field]');

  const paint = (el) => {
    const field = fieldOf(el);
    if (!field) return true;
    const error = field.querySelector('.dist-form__error');
    const valid = el.validity.valid;
    const filled = el.type === 'checkbox' ? el.checked : el.value !== '';

    el.setAttribute('aria-invalid', String(!valid));
    field.classList.toggle('is-valid', valid && (el.required || filled));

    if (valid) {
      field.classList.remove('is-invalid');
      if (error) {
        error.textContent = '';
        error.hidden = true;
      }
      return true;
    }

    if (error) {
      error.textContent = message(el);
      error.hidden = false;
    }
    /* The message is in the DOM before the class lands, so it can fade in. */
    requestAnimationFrame(() => field.classList.add('is-invalid'));
    return false;
  };

  const fixScheme = (el) => {
    const value = el.value.trim();
    if (value && !/^https?:\/\//i.test(value)) el.value = `https://${value}`;
  };

  const markEmpty = (select) => select.classList.toggle('is-empty', select.value === '');

  /* --------------------------------------------------- one round trip */

  const stash = (form) => {
    try {
      const data = {};
      new FormData(form).forEach((value, name) => {
        if (name.startsWith('contact[') && name !== 'contact[Fax]' && name !== 'contact[email]') data[name] = value;
      });
      sessionStorage.setItem(STORE, JSON.stringify(data));
    } catch (e) {
      /* storage may be unavailable; the form still posts */
    }
  };

  const restore = (form) => {
    try {
      const raw = sessionStorage.getItem(STORE);
      sessionStorage.removeItem(STORE);
      if (!raw) return;
      const data = JSON.parse(raw);
      Object.keys(data).forEach((name) => {
        const el = form.elements[name];
        if (!el) return;
        if (el.type === 'checkbox') el.checked = data[name] === el.value;
        else el.value = data[name];
        if (el.tagName === 'SELECT') markEmpty(el);
      });
    } catch (e) {
      /* nothing to put back */
    }
  };

  /* ---------------------------------------------------------------- form */

  const initForm = (root) => {
    if (root.dataset.ready) return;
    root.dataset.ready = 'true';

    const done = root.querySelector('[data-done]');
    if (done) {
      try {
        sessionStorage.removeItem(STORE);
      } catch (e) {
        /* fine */
      }
      if (!reduced.matches) {
        done.classList.add('is-armed');
        requestAnimationFrame(() => requestAnimationFrame(() => done.classList.add('is-in')));
      }
      if (document.activeElement !== done) done.focus({ preventScroll: false });
      return;
    }

    const form = root.querySelector('form');
    if (!form) return;

    const controls = Array.from(form.querySelectorAll('.dist-form__control, .dist-form__check'));
    const trap = form.querySelector('[data-trap]');
    const button = form.querySelector('[type="submit"]');

    /* The server only ever rejects the email; when it does, put the rest back. */
    if (root.querySelector('.is-invalid, [role="alert"]')) restore(form);

    form.noValidate = true;
    form.addEventListener('invalid', (event) => event.preventDefault(), true);

    controls.forEach((el) => {
      if (el.tagName === 'SELECT') {
        el.querySelectorAll('option[value="---"]').forEach((o) => o.remove());
        markEmpty(el);
        el.addEventListener('change', () => {
          markEmpty(el);
          paint(el);
        });
        return;
      }
      if (el.type === 'checkbox') {
        el.addEventListener('change', () => paint(el));
        return;
      }
      el.addEventListener('blur', () => {
        if (el.type === 'url') fixScheme(el);
        if (el.value !== '' || el.required) paint(el);
      });
      el.addEventListener('input', () => {
        const field = fieldOf(el);
        if (field && field.classList.contains('is-invalid') && el.validity.valid) paint(el);
      });
    });

    form.addEventListener('submit', (event) => {
      if (root.classList.contains('is-submitting')) return;

      if (trap && trap.value !== '') {
        event.preventDefault();
        return;
      }

      let first = null;
      controls.forEach((el) => {
        const ok = paint(el);
        if (!ok && !first) first = el;
      });

      if (first) {
        event.preventDefault();
        const target = first.type === 'checkbox' ? fieldOf(first) : first;
        target.scrollIntoView({ block: 'center', behavior: reduced.matches ? 'auto' : 'smooth' });
        first.focus({ preventScroll: true });
        return;
      }

      stash(form);
      root.classList.add('is-submitting');
      if (button) {
        button.setAttribute('aria-disabled', 'true');
        /* After the entry list is built, so the post carries every field. */
        setTimeout(() => {
          button.disabled = true;
        }, 0);
      }
    });

    /* Back from the confirmation, or a cancelled navigation: the button comes back. */
    window.addEventListener('pageshow', (event) => {
      if (!event.persisted) return;
      root.classList.remove('is-submitting');
      if (button) {
        button.disabled = false;
        button.removeAttribute('aria-disabled');
      }
    });
  };

  /* -------------------------------------------------------------- header */

  const armIntro = (intro) => {
    intro.classList.remove('is-in', 'is-armed');
    if (reduced.matches) return;
    intro.classList.add('is-armed');
    requestAnimationFrame(() => requestAnimationFrame(() => intro.classList.add('is-in')));
  };

  /* ---------------------------------------------------------------- boot */

  document.querySelectorAll('[data-dist-form]').forEach(initForm);

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('[data-dist-form]').forEach(initForm);
    event.target.querySelectorAll('[data-dist-intro]').forEach(armIntro);
  });

  document.addEventListener('shopify:section:select', (event) => {
    event.target.querySelectorAll('[data-dist-intro].is-armed').forEach((intro) => intro.classList.add('is-in'));
  });
})();
